import { useEffect, useMemo, useRef } from 'react'
import { CapturedActionList } from '@renderer/overlay/Mini/components/mini/nested/captured-actions'
import { useStagedActionsQuery } from '@renderer/overlay/Sidepanel/hooks/staged-actions/query'
import type { StagedAction } from '@renderer/overlay/Sidepanel/hooks/staged-actions/types'
import { actionItemOf } from '@renderer/overlay/Sidepanel/views/meeting-view/action-items/meeting-action-items'
import { toastManager } from '../toast-store'

interface NotchActionToastProps {
  actions: Array<StagedAction>
  label: string
  toastId: string
}

/** The action toast in the redesigned notch: the captured list's rows, read live; a dismissed row drops out. */
export function NotchActionToast({ actions, label, toastId }: NotchActionToastProps) {
  const { allActions } = useStagedActionsQuery()
  // Once the cache has loaded, a row missing from it was dismissed, as the corner toast treats it
  const hasLoadedRef = useRef(false)
  if (allActions.length > 0) hasLoadedRef.current = true
  const remaining = useMemo(() => {
    const liveById = new Map(allActions.map((action) => [action.id, action]))
    return actions.flatMap((action) => {
      const live = liveById.get(action.id) ?? (hasLoadedRef.current ? undefined : action)
      return !live || live.archivedAt ? [] : [live]
    })
  }, [actions, allActions])

  useEffect(() => {
    if (remaining.length === 0) toastManager.dismiss(toastId, 'emptied')
  }, [remaining.length, toastId])

  const items = useMemo(() => remaining.map(actionItemOf), [remaining])
  return <CapturedActionList items={items} label={label} source="notch toast" />
}
